import {AuthPipeGenerator, loggedIn, redirectLoggedInTo, redirectUnauthorizedTo} from '@angular/fire/auth-guard';
import {inject, Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Data,
  GuardResult,
  MaybeAsync,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import {map, take} from 'rxjs';
import {AuthService} from '../services/auth.service';
import {UserModel} from '../models/user.model';
import {LoggerService} from '../services/logger.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
  private readonly logger = inject(LoggerService).get('auth-guard')

  constructor(
    private readonly auth: AuthService,
    private readonly router: Router
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.check(next.data, next, state);
  }

  canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.check(next.parent!.data, next, state);
  }

  private check(data: Data, next: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> {
    const pipeFn: AuthPipeGenerator = (data['authGuardPipe']) || (() => loggedIn);
    const authPipe = pipeFn(next, state);

    return this.auth.currentUser.pipe(
      take(1),
      map((appUser: UserModel | null) => appUser?.authUser || null),
      authPipe,
      map((result: any) => {
        if (typeof result === 'boolean') {
          this.logger.debug(`User is${result ? '' : ' not'} logged in`);
          return result;
        } else if (Array.isArray(result)) {
          this.logger.debug(`Directing to ${result}`);
          return this.router.createUrlTree(result);
        } else {
          this.logger.debug(`Directing to ${result}`);
          return this.router.parseUrl(result);
        }
      })
    )
  }
}

export const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['/login']);
export const redirectLoggedInToHome = () => redirectLoggedInTo(['/home']);
